/**
 * Confirm leaving a My Task manual-assessment form via Cancel.
 * Figma: GRI UI/UX DESIGN node 6854-122775 (cancel performing a task)
 */

import CloseIcon from "@mui/icons-material/Close";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import { DIALOG_CLOSE_BUTTON_SX } from "../../constants/dialogCloseButtonStyles";
import { withoutBackdropClose } from "../../utils/withoutBackdropClose";

export type ManualAssessmentCancelConfirmDialogProps = Readonly<{
  open: boolean;
  onContinueEditing: () => void;
  onDiscardAndLeave: () => void;
}>;

const TITLE = "Leave this page?";
const BODY =
  "Unsaved changes will be lost and you will be redirected to My Task page.";

const DIALOG_PAPER_SX = {
  width: 480,
  maxWidth: "calc(100vw - 32px)",
  borderRadius: "3px",
  boxSizing: "border-box" as const,
};

const TITLE_SX = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  fontFamily: '"Nunito Sans", sans-serif',
  fontSize: 18,
  lineHeight: "28px",
  color: "#172B4D",
  fontWeight: 700,
  pr: 1,
} as const;

const BODY_SX = {
  fontFamily: '"Nunito Sans", sans-serif',
  fontSize: 14,
  lineHeight: "20px",
  color: "#172B4D",
} as const;

const TEXT_BUTTON_SX = {
  textTransform: "none" as const,
  fontFamily: '"Nunito Sans", sans-serif',
  fontSize: 14,
  fontWeight: 600,
  color: "#295EF1",
};

const CONFIRM_BUTTON_SX = {
  textTransform: "none" as const,
  fontFamily: '"Nunito Sans", sans-serif',
  fontSize: 14,
  fontWeight: 600,
  boxShadow: "none",
};

export default function ManualAssessmentCancelConfirmDialog({
  open,
  onContinueEditing,
  onDiscardAndLeave,
}: ManualAssessmentCancelConfirmDialogProps) {
  return (
    <Dialog
      open={open}
      onClose={withoutBackdropClose(onContinueEditing)}
      maxWidth={false}
      PaperProps={{ sx: DIALOG_PAPER_SX }}
      aria-labelledby="manual-assessment-cancel-confirm-title"
    >
      <DialogTitle
        id="manual-assessment-cancel-confirm-title"
        sx={TITLE_SX}
      >
        {TITLE}
        <IconButton
          aria-label="Close"
          onClick={onContinueEditing}
          sx={DIALOG_CLOSE_BUTTON_SX}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 0, pb: 1 }}>
        <Typography sx={BODY_SX}>{BODY}</Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button
          variant="text"
          onClick={onContinueEditing}
          sx={TEXT_BUTTON_SX}
        >
          Continue editing
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={onDiscardAndLeave}
          sx={CONFIRM_BUTTON_SX}
        >
          Discard and leave
        </Button>
      </DialogActions>
    </Dialog>
  );
}
